import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import QRCode from 'qrcode.react';
import { useMediaQuery } from 'react-responsive';

import DesignEditor from '../../../../../../components/global/DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import DesignEditorControls, {
  DesignEditorPrimaryControls,
  DesignEditorSecondaryControls,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorControls';
import DesignEditorPrimaryControlButton from '../../../../../../components/global/DesignEditor/components/DesignEditorControls/components/DesignEditorPrimaryControlButton';
import SecondaryElementsView from './components/SecondaryElementsView';
import BackgroundIcon from '../../../../../../components/icons/BackgroundIcon';
import ElementsIcon from '../../../../../../components/icons/ElementsIcon';
import SecondaryBackgroundView from './components/SecondaryBackgroundView';
import DesignEditorPreviewPane, {
  DesignEditorPreviewContent,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import useAlbumSlideshow from '../hooks/useAlbumSlideshow';
import SlideshowThumbnailMenu from './components/SlideshowThumbnailMenu';
import Spinner from '../../../../../../components/global/Spinner';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import useRequestConfirm from '../../../../../../hooks/useRequestConfirm';
import CheckmarkIcon from '../../../../../../components/icons/CheckmarkIcon';
import PhotoSelectionOverlayModal, {
  ALBUM_SLIDESHOW_DESIGNER_PHOTO_SELECTION_MODAL,
} from './components/PhotoSelectionOverlayModal';
import useSlideshowPlayback from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/hooks/useSlideshowPlayback';
import { calculateSlideshowDuration } from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/helpers';
import { breakpointDown } from '../../../../../../services/responsive/responsive';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../constants/albums';
import { getMaxThumb } from '../../../../../../util/photo';
import AlbumSlideshowShareModal, {
  ALBUM_SLIDESHOW_SHARE_MODAL,
} from '../../../../../../components/global/AlbumSlideshowShareModal';
import { openAlert } from '../../../../../../actions/modal-actions';
import { getErrorString } from '../../../../../../util/normalize-errors';
import TooltipButton from '../../../../../../components/global/TooltipButton';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowDesignerDismiss,
  postNativeAppAlbumSlideshowDesignerUpdateError,
  postNativeAppAlbumSlideshowDesignerShareDialog,
} from '../../../../../../services/native-bridge/native-apps-interface';
import { CONTRIBUTOR_UPLOAD_ACCESS } from '../../../../../../constants/contributors';
import { QR_CODE_INSERT_MODAL } from './components/QRCodeInsertModal';
import ShareIcon from '../../../../../../components/icons/ShareIcon';
import PublishedSlideshowShareModal, {
  PUBLISHED_SLIDESHOW_SHARE_MODAL,
} from './components/PublishedSlideshowShareModal';

const CONTROL_PANE = {
  ELEMENTS: 'ELEMENTS',
  BACKGROUND: 'BACKGROUND',
};

const propTypes = {
  basename: PropTypes.string,
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string,
    }),
  }),
};

const AlbumSlideshowDesignerView = ({ basename, history, match }) => {
  const { albumId } = match.params;
  const [activeControlPane, setActiveControlPane] = useState(
    CONTROL_PANE.ELEMENTS,
  );
  const {
    saving,
    album,
    slideshow,
    onSlideshowUpdate,
    onSlideshowDelete,
    loadingMore,
    onLoadMore,
    isOwner,
  } = useAlbumSlideshow({ albumId });
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const requestConfirm = useRequestConfirm();
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const isNativeView = isNativeAppWebView();

  // Slideshow updating related.
  const [slideshowPhotos, setSlideshowPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [excludedPhotos, setExcludedPhotos] = useState([]);
  const [deletedSlideIds, setDeletedSlideIds] = useState([]);
  const [slideDurationMs, setSlideDurationMs] = useState(5000);
  const [activePhoto, setActivePhoto] = useState(null);
  const [qrCode, setQrCode] = useState({ isEnabled: false });
  const [tickerTape, setTickerTape] = useState({ message: '' });
  const [slideBackgroundColor, setSlideBackgroundColor] = useState('#000000');
  const [slideBackgroundImage, setSlideBackgroundImage] = useState(null);

  useEffect(() => {
    if (slideshow?.slide_duration_ms) {
      setSlideDurationMs(slideshow.slide_duration_ms);
    }
    if (slideshow?.photos) {
      setSlideshowPhotos(
        slideshow?.intro_asset_image
          ? [
              { asset_image: slideshow.intro_asset_image },
              ...slideshow.photos.records,
            ]
          : slideshow.photos.records,
      );
      if (
        activePhoto &&
        !(slideshow?.photos?.records ?? []).find(
          slideshowPhoto => slideshowPhoto.photo.id === activePhoto.photo?.id,
        )
      ) {
        setActivePhoto(null);
      }
    }
    if (slideshow?.excluded_photos) {
      setExcludedPhotos(slideshow.excluded_photos.records);
    }
    if (slideshow?.selected_photos) {
      setSelectedPhotos(slideshow.selected_photos.records);
    }
    if (slideshow?.slide_background_color) {
      setSlideBackgroundColor(slideshow.slide_background_color);
    }
    // This can be null, so we need to account for it.
    if (typeof slideshow?.slide_background_asset_image !== 'undefined') {
      setSlideBackgroundImage(slideshow.slide_background_asset_image);
    }
    if (slideshow?.ticker) {
      setTickerTape({
        message: slideshow.ticker.message,
        color: slideshow.ticker.color,
        backgroundColor: slideshow.ticker.background_color,
      });
    }
    if (slideshow?.qr_code) {
      setQrCode({
        url: `${album?.auto_contributor_share_url}?slideshow=1`,
        isEnabled: slideshow.qr_code.is_enabled,
        withCallout: slideshow.qr_code.display_callout,
        withReviewPhotos:
          album?.auto_contributor_settings?.upload_access ===
          CONTRIBUTOR_UPLOAD_ACCESS.REVIEW,
      });
    }
    // Reset deleted slide ids, since the slideshow has been updated
    // this avoids the issue of having deleted ids stored and then
    // trying to delete them.
    setDeletedSlideIds([]);
  }, [album, slideshow]);

  const activePhotoIdx = slideshowPhotos.findIndex(
    slideshowPhoto =>
      (slideshowPhoto.asset_image &&
        activePhoto?.asset_image?.id === slideshowPhoto.asset_image.id) ||
      (slideshowPhoto.photo &&
        activePhoto?.photo?.id === slideshowPhoto.photo.id),
  );
  const nextActivePhotoId = (() => {
    if (activePhotoIdx === -1) {
      return null;
    }
    // Since this will always be the 2nd or greatest, don't care for asset_image
    if (activePhotoIdx >= 0 && activePhotoIdx < slideshowPhotos.length - 1) {
      return slideshowPhotos[activePhotoIdx + 1].photo.id;
    }

    return slideshowPhotos[0].asset_image?.id ?? slideshowPhotos[0].photo?.id;
  })();

  const {
    isPlaying,
    onPlaybackStart,
    onPlaybackStop,
    onThumbnailClick,
    elapsedTime,
    slideDuration,
    slideRemainingTime,
    slideshowDuration,
    skipFadeIn,
  } = useSlideshowPlayback({
    slideshowPhotos,
    slideshowPhoto: activePhoto,
    slideshowPhotoIdx: slideshowPhotos.findIndex(
      slideshowPhoto =>
        (slideshowPhoto.asset_image &&
          activePhoto?.asset_image?.id === slideshowPhoto.asset_image.id) ||
        (slideshowPhoto.photo &&
          activePhoto?.photo?.id === slideshowPhoto.photo.id),
    ),
    nextPhotoId: nextActivePhotoId,
    slideDurationMs,
    slideshowDuration:
      typeof slideshow?.duration !== 'undefined'
        ? slideshow.duration * 1000
        : calculateSlideshowDuration(slideshowPhotos, slideDurationMs),
    autoPlay: false,
    allowDeselect: true,
    intervalValue: 50,
    onChangePhoto: ({ nextPhotoId }) => {
      if (
        nextPhotoId === (activePhoto?.asset_image ?? activePhoto?.photo)?.id
      ) {
        setActivePhoto(null);
      } else if (nextPhotoId !== null) {
        setActivePhoto(
          slideshowPhotos.find(
            slideshowPhoto =>
              (slideshowPhoto.asset_image &&
                nextPhotoId === slideshowPhoto.asset_image.id) ||
              (slideshowPhoto.photo && nextPhotoId === slideshowPhoto.photo.id),
          ),
        );
      }
    },
  });

  const photoOpacity = (() => {
    if (!isPlaying) {
      return 1.0;
    }

    const fadeThreshold = slideDuration * 0.2;
    // If we have > duration - threshold remaining time (fade-out)
    if (!skipFadeIn && slideRemainingTime > slideDuration - fadeThreshold) {
      return (slideDuration - slideRemainingTime) / 1000.0;
    }
    // If we have < remaining time (fade-out)
    if (slideRemainingTime <= fadeThreshold) {
      return slideRemainingTime / fadeThreshold;
    }
    return 1.0;
  })();

  const onSaveSlideshow = async (
    status = ALBUM_SLIDESHOW_STATUS.DRAFT,
    mode = ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
    currentExcludedPhotos = excludedPhotos,
    currentSelectedPhotos = selectedPhotos,
  ) => {
    try {
      const createExcludedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
          ? currentExcludedPhotos
              .filter(
                slideshowPhoto =>
                  !(slideshow?.excluded_photos?.records ?? []).find(
                    ({ photo }) => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const deleteExcludedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
          ? (slideshow?.excluded_photos?.records ?? [])
              .filter(
                ({ photo }) =>
                  !currentExcludedPhotos.find(
                    slideshowPhoto => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const createSelectedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? currentSelectedPhotos
              .filter(
                slideshowPhoto =>
                  !(slideshow?.selected_photos?.records ?? []).find(
                    ({ photo }) => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const deleteSelectedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? (slideshow?.selected_photos?.records ?? [])
              .filter(
                ({ photo }) =>
                  !currentSelectedPhotos.find(
                    slideshowPhoto => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      await onSlideshowUpdate({
        status,
        mode,
        reviewPhotos: qrCode.isEnabled ? qrCode.withReviewPhotos : undefined,
        slideDurationMs,
        slideBackgroundColor,
        slideBackgroundAssetImage: slideBackgroundImage
          ? {
              asset_image_id:
                slideBackgroundImage.id ===
                slideshow?.slide_background_asset_image?.id
                  ? undefined
                  : slideBackgroundImage.id,
              existing_asset_image_id:
                slideshow?.slide_background_asset_image?.id,
            }
          : null,
        createExcludedPhotos,
        deleteExcludedPhotos,
        createSelectedPhotos,
        deleteSelectedPhotos,
        deletedSlideshowSlideIds: deletedSlideIds,
        qrCode: qrCode.isEnabled
          ? { is_enabled: true, display_callout: qrCode.withCallout }
          : undefined,
        ticker: tickerTape.message
          ? {
              message: tickerTape.message,
              color: tickerTape.color,
              background_color: tickerTape.backgroundColor,
            }
          : undefined,
      });
    } catch (e) {
      const errorString = getErrorString(e);
      if (isNativeView) {
        postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
      } else {
        openAlert(errorString);
      }
    }
  };

  const activeThumb = (() => {
    if (activePhoto && activePhoto.asset_image) {
      return activePhoto.asset_image;
    }
    if (activePhoto) {
      return getMaxThumb(activePhoto.photo.thumbnail_urls);
    }
    return null;
  })();

  // If not the owner, you should not be here.
  if (!isOwner) {
    history.replace(
      `${basename ? `/app/${basename}` : ''}/albums/${albumId}/slideshow`,
    );
  }

  if (!slideshow) {
    return (
      <div className="w-100vw h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }
  return (
    <DesignEditor className="slideshow-designer">
      <DesignEditorHeader>
        <DesignEditorHeaderLeftSlot>
          <Button
            btnStyle="link"
            className="mr-2"
            onClick={() => {
              if (isNativeView) {
                // Instead of letting mobile determine the next place to go
                //  handle this in such a way that the user goes back to the
                //  slideshow.
                history.push(`/albums/${albumId}/slideshow`);
              } else {
                history.goBack();
              }
            }}
          >
            <Angle direction="left" />{' '}
            <span className="d-none d-md-inline-block ml-1">Back</span>
          </Button>
        </DesignEditorHeaderLeftSlot>
        <DesignEditorHeaderRightSlot>
          {slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED && (
            <Fragment>
              <Button
                onClick={() =>
                  requestConfirm({
                    message: (
                      <div>
                        <h3 className="text-center">Unpublish Slideshow?</h3>
                        <p className="text-center">
                          Your slideshow will be saved as a draft after
                          unpublishing it. Others will not be able to view or
                          share this slideshow until it is published again.
                        </p>
                      </div>
                    ),
                    cancelText: 'Go back',
                    confirmText: 'Unpublish',
                    onConfirm: async () =>
                      onSaveSlideshow(
                        ALBUM_SLIDESHOW_STATUS.DRAFT,
                        slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                      ),
                    onCancel: () => {},
                    confirmBtnStyle: 'danger',
                    confirmBtnOutline: true,
                  })
                }
                className="slideshow-designer-published-button"
                disabled={saving}
              >
                <CheckmarkIcon
                  className="default-child"
                  color="white"
                  size={16}
                />
                <span className="default-child ml-1">
                  {saving ? 'Saving...' : 'Published'}
                </span>
                <span className="hover-child ml-1">
                  {saving ? 'Saving...' : 'Unpublish'}
                </span>
              </Button>
              <Button
                btnStyle="reset"
                className="published-slideshow-share-btn"
                onClick={() => openModal(PUBLISHED_SLIDESHOW_SHARE_MODAL)}
              >
                <ShareIcon color="#0d84fe" />
              </Button>
            </Fragment>
          )}
          {slideshow?.status === ALBUM_SLIDESHOW_STATUS.DRAFT && (
            <TooltipButton
              tooltipId="publish-btn-tooltip"
              tip={
                <Fragment>
                  Published slideshows can be viewed and shared by members of
                  your event, as well as be embedded in webpages.
                </Fragment>
              }
              onClick={async () => {
                try {
                  await onSaveSlideshow(
                    ALBUM_SLIDESHOW_STATUS.PUBLISHED,
                    slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                  );
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerShareDialog();
                  } else {
                    openModal(ALBUM_SLIDESHOW_SHARE_MODAL, {
                      modalTitle: (
                        <p className="w-100 mb-0 text-center h3">
                          Your slideshow has been published!
                        </p>
                      ),
                    });
                  }
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Publish'}
            </TooltipButton>
          )}
        </DesignEditorHeaderRightSlot>
      </DesignEditorHeader>

      <DesignEditorControls>
        <DesignEditorPrimaryControls>
          <DesignEditorPrimaryControlButton
            active={activeControlPane === CONTROL_PANE.ELEMENTS}
            icon={<ElementsIcon />}
            title="Elements"
            onClick={() => {
              setActiveControlPane(CONTROL_PANE.ELEMENTS);
            }}
          />
          <DesignEditorPrimaryControlButton
            active={activeControlPane === CONTROL_PANE.BACKGROUND}
            icon={<BackgroundIcon />}
            title="Background"
            onClick={() => {
              setActiveControlPane(CONTROL_PANE.BACKGROUND);
            }}
          />
        </DesignEditorPrimaryControls>
        <DesignEditorSecondaryControls>
          <SecondaryElementsView
            visible={activeControlPane === CONTROL_PANE.ELEMENTS}
            albumId={albumId}
            qrCode={qrCode}
            tickerTape={tickerTape}
            onInsertIntroSlide={async ({ assetImage, existing }) => {
              if (saving) {
                return;
              }
              try {
                await onSlideshowUpdate({
                  introAssetImage: {
                    asset_image_id: !existing ? assetImage.id : undefined,
                    existing_asset_image_id: existing
                      ? assetImage.id
                      : slideshow?.intro_asset_image?.id,
                  },
                });
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            }}
            onUpdateQRCode={async ({ withReviewPhotos, enabled }) => {
              if (saving) {
                return;
              }
              try {
                await onSlideshowUpdate({
                  reviewPhotos: withReviewPhotos,
                  qrCode: {
                    is_enabled: enabled,
                    display_callout: true,
                  },
                });
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            }}
            onInsertTickerTape={async ({
              message,
              color: tickerTapeColor,
              backgroundColor: tickerTapeBg,
            }) => {
              if (saving) {
                return;
              }
              try {
                await onSlideshowUpdate({
                  ticker: {
                    message,
                    color: tickerTapeColor,
                    background_color: tickerTapeBg,
                  },
                });
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            }}
          />
          <SecondaryBackgroundView
            visible={activeControlPane === CONTROL_PANE.BACKGROUND}
            saving={saving}
            backgroundColor={slideBackgroundColor}
            backgroundImage={slideBackgroundImage}
            onBackgroundColorChange={async newColor => {
              if (saving) {
                return;
              }
              try {
                await onSlideshowUpdate({
                  slideBackgroundColor: newColor,
                });
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            }}
            onBackgroundImageChange={async newAssetImage => {
              if (saving) {
                return;
              }
              try {
                await onSlideshowUpdate({
                  slideBackgroundAssetImage: newAssetImage
                    ? {
                        asset_image_id:
                          newAssetImage.id ===
                          slideshow?.slide_background_asset_image?.id
                            ? undefined
                            : newAssetImage.id,
                        existing_asset_image_id:
                          slideshow?.slide_background_asset_image?.id,
                      }
                    : null,
                });
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            }}
          />
        </DesignEditorSecondaryControls>
        <SlideshowThumbnailMenu
          isPlaying={isPlaying}
          onPlayToggle={() =>
            isPlaying ? onPlaybackStop() : onPlaybackStart()
          }
          slideshowPhotos={slideshowPhotos}
          photosTotal={
            (slideshow?.photos?.total ?? 0) +
            (slideshow?.intro_asset_image ? 1 : 0)
          }
          photosOffset={Math.max(
            (slideshow?.photos?.records?.length ?? 10) - 10,
            0,
          )}
          elapsedTime={elapsedTime}
          currentPhotoId={
            activePhoto?.asset_image?.id ?? activePhoto?.photo?.id
          }
          currentSlideDuration={slideDuration}
          currentSlideRemainingTime={slideRemainingTime}
          totalDuration={slideshowDuration}
          defaultDuration={slideDurationMs}
          onDefaultDurationChange={async newMsDuration => {
            onPlaybackStop();
            if (saving) {
              return;
            }
            try {
              await onSlideshowUpdate({
                slideDurationMs: newMsDuration,
              });
            } catch (e) {
              const errorString = getErrorString(e);
              if (isNativeView) {
                postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
              } else {
                openAlert(errorString);
              }
            }
          }}
          onThumbnailClick={onThumbnailClick}
          onAddPhoto={() => {
            onPlaybackStop();
            openModal(ALBUM_SLIDESHOW_DESIGNER_PHOTO_SELECTION_MODAL);
          }}
          onDeletePhoto={async ({ photoId, photoIdx, isIntroSlide }) => {
            if (slideshow && isIntroSlide) {
              await onSlideshowUpdate({
                introAssetImage: null,
              });
              return;
            }
            if (slideshow) {
              await onSlideshowUpdate({
                createExcludedPhotos:
                  slideshow.mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
                    ? [{ photo_id: photoId }]
                    : undefined,
                deleteSelectedPhotos:
                  slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
                    ? [{ photo_id: photoId }]
                    : undefined,
              });
            }
            const nextPhotoIdx = (() => {
              if (
                photoIdx < slideshowPhotos.length - 1 &&
                slideshowPhotos.length > 1
              ) {
                return photoIdx + 1;
              }
              if (
                photoIdx === slideshowPhotos.length - 1 &&
                slideshowPhotos.length > 1
              ) {
                return photoIdx - 1;
              }
              return null;
            })();
            if (nextPhotoIdx === null) {
              setActivePhoto(null);
            } else {
              setActivePhoto(slideshowPhotos[nextPhotoIdx]);
            }
          }}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </DesignEditorControls>

      <DesignEditorPreviewPane>
        <DesignEditorPreviewContent>
          <div
            className="slideshow-designer-preview-container"
            style={{
              backgroundColor: slideBackgroundColor,
              backgroundImage: slideBackgroundImage
                ? `url(${slideBackgroundImage.url})`
                : '',
            }}
          >
            {activeThumb && (
              <img
                src={activeThumb.url}
                alt="Active Slide"
                style={{ opacity: photoOpacity }}
              />
            )}
            {qrCode?.isEnabled && (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <div
                tabIndex={0}
                role="button"
                className="qr-code-container"
                onClick={() => {
                  openModal(QR_CODE_INSERT_MODAL, {
                    enabled: !!qrCode.isEnabled,
                    insertCallout: true,
                    includeLogo: !!qrCode?.withLogo,
                    reviewPhotos: !!qrCode?.withReviewPhotos,
                  });
                }}
              >
                <QRCode
                  id="got-photos-qr"
                  value={qrCode.url}
                  renderAs="canvas"
                  size={isMobile ? 60 : 100}
                  imageSettings={
                    qrCode.withLogo
                      ? {
                          src: qrCode.logo.url,
                          height: 64,
                          width: 64,
                        }
                      : undefined
                  }
                />
                {qrCode.withCallout && (
                  <div className="qr-code-callout">SCAN TO ADD PHOTOS</div>
                )}
              </div>
            )}
            {tickerTape?.message && (
              <div
                className="ticker-tape-container"
                style={{ backgroundColor: tickerTape.backgroundColor }}
              >
                <p style={{ color: tickerTape.color }}>{tickerTape.message}</p>
              </div>
            )}
          </div>
        </DesignEditorPreviewContent>
      </DesignEditorPreviewPane>
      <PublishedSlideshowShareModal
        album={album}
        albumId={albumId}
        slideshow={slideshow}
        onUnpublish={() =>
          requestConfirm({
            message: (
              <div>
                <h3 className="text-center">Unpublish Slideshow?</h3>
                <p className="text-center">
                  Your slideshow will be saved as a draft after unpublishing it.
                  Others will not be able to view or share this slideshow until
                  it is published again.
                </p>
              </div>
            ),
            cancelText: 'Go back',
            confirmText: 'Unpublish',
            onConfirm: async () => {
              onSaveSlideshow(
                ALBUM_SLIDESHOW_STATUS.DRAFT,
                slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
              );
              closeModal(PUBLISHED_SLIDESHOW_SHARE_MODAL);
            },
            onCancel: () => {},
            confirmBtnStyle: 'danger',
            confirmBtnOutline: true,
          })
        }
        onDelete={() =>
          requestConfirm({
            message: (
              <div>
                <h3 className="text-center">Delete slideshow?</h3>
                <p className="text-center">
                  Your slideshow will be deleted from the platform entirely. All
                  photos will be kept on the album, but share links and embed
                  codes for the slideshow will stop working. Are you sure you
                  want to delete this slideshow?
                </p>
              </div>
            ),
            cancelText: 'Go back',
            confirmText: 'Yes, Delete',
            onConfirm: async () => {
              try {
                await onSlideshowDelete();
                closeModal(PUBLISHED_SLIDESHOW_SHARE_MODAL);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerDismiss();
                } else {
                  history.push(
                    `${
                      basename
                        ? `/app/${basename}/albums/${albumId}/photos`
                        : '/app'
                    }`,
                  );
                }
              } catch (e) {
                const errorString = getErrorString(e);
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                } else {
                  openAlert(errorString);
                }
              }
            },
            onCancel: () => {},
            confirmBtnStyle: 'danger',
            confirmBtnOutline: true,
          })
        }
      />
      <AlbumSlideshowShareModal
        album={album}
        albumId={albumId}
        designerOptions={{
          onPresent: () => {
            history.push(
              `${
                basename ? `/app/${basename}` : ''
              }/albums/${albumId}/slideshow`,
            );

            closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
          },
          // If we don't have a basename, going back to the event will not be available
          // this is to avoid directing the user to the wrong basename, which will, in
          // the end, result in the user going back to the home page.
          onGoBackToEvent: basename
            ? () => {
                history.push(`/app/${basename}/albums/${albumId}/photos`);
                closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
              }
            : undefined,
        }}
      />
      <PhotoSelectionOverlayModal
        albumId={albumId}
        currentSelectionMode={
          slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
        }
        currentExcludedPhotos={excludedPhotos}
        currentSelectedPhotos={selectedPhotos}
        onChange={async ({
          selectionMode: newSelectionMode,
          excludedPhotos: newExcludedPhotos,
          selectedPhotos: newSelectedPhotos,
        }) => {
          await onSaveSlideshow(
            slideshow?.status ?? ALBUM_SLIDESHOW_STATUS.DRAFT,
            newSelectionMode,
            // Encapsulate the photos, since we want an object
            // akin to AlbumSlideshowPhoto, which encapsulates a photo.
            newExcludedPhotos.map(photo => ({ photo })),
            newSelectedPhotos.map(photo => ({ photo })),
          );
        }}
      />
    </DesignEditor>
  );
};

AlbumSlideshowDesignerView.propTypes = propTypes;

export default withRouter(AlbumSlideshowDesignerView);
